import { FormControl } from '@material-ui/core'
import { SERVICE_OFFERING_OPTIONS } from 'constants/sectors'
import { authSelectors } from 'ducks/auth'
import CustomField from 'elements/field/CustomField'
import CheckboxMultiSelectInput from 'elements/input/CheckboxMultiSelectInput'
import UiContainer from 'elements/UiContainer'
import { ComponentVersionsInherit, ComponentVersions_3_0, Typography } from 'opensolar-ui'
import { TextInputProps } from 'ra-ui-materialui/src/input/TextInput'
import { Fragment, useMemo } from 'react'
import {
  AutocompleteInput,
  Edit,
  FormDataConsumer,
  ImageField,
  ImageInput,
  ReferenceInput,
  SimpleForm,
  TextInput,
  useTranslate,
} from 'react-admin'
import { useSelector } from 'react-redux'
import { orgSelectors } from 'ducks/orgs'
import { formatTaxIdentifierHintText } from 'resources/wallet/useTaxIdentifierLabel'
import type { BusinessRegNumber } from 'types/orgs'
import ImageFieldClearable from '../../elements/field/ImageFieldClearable'
import { ColorInput } from '../../elements/input/ColorInput'
import EditorComponent from '../../elements/wysiwyg/EditorComponentWrapper'
import { generateDefaultValueFunc, getRoleFromState, renderDraftEditor, renderRawHtmlEditor } from '../../util/misc'
import { validateOrgContentEdit } from '../../validations/Orgs'

export const FroalaEditorWithAboutContent = ({ record, ...rest }) => {
  return (
    <CustomField
      component={EditorComponent}
      fieldName="about_content"
      name="about_content"
      source="about_content"
      mentionContext="org"
      allowUploadImage={true}
      {...rest}
      label={'About content'}
    />
  )
}

export const ShowHighLightColor = (props) => {
  const highlightColor = props.value
  return (
    <span
      style={{
        position: 'absolute',
        display: 'inline-block',
        top: 44,
        right: 10,
        width: 20,
        height: 20,
        borderRadius: '50%',
        zIndex: -1,
        background: highlightColor,
      }}
    />
  )
}

export const contentFields = [
  'name',
  'country',
  'address',
  'locality',
  'zip',
  'state',
  'sales_phone_number',
  'company_email',
  'company_website',
  'about_content',
  'logo_file_contents',
  'enable_exhibit',
  'color_highlight',
]

const inputStyle = {
  width: 256,
  maxWidth: 256,
  marginRight: 40,
}

const shortInputStyle = {
  width: 108,
  marginRight: 0,
}

type BusinessRegNumbersProps = {
  businessRegNumbers: BusinessRegNumber[]
  typeFilter?: 'gs_tax_number' | 'company_registration_number' | 'qualification_number'
  limit?: number
  sourcePrefix?: string
}

const brnStyleProps: Record<string, Partial<TextInputProps>> = {
  business_reg_numbers: {
    style: inputStyle,
  },
  'org_registration.business_reg_numbers': {
    fullWidth: true,
    variant: 'outlined',
  },
}

/**
 * A component to display a list of business registration number inputs.
 * Reused in org ContentEdit and Ada registration flow.
 */
const BusinessRegistrationNumberInput = ({
  businessRegNumbers,
  typeFilter,
  limit,
  sourcePrefix = 'business_reg_numbers',
}: BusinessRegNumbersProps) => {
  const translate = useTranslate()
  const list = (businessRegNumbers ?? [])
    .map((brn, originalIndex) => ({ brn, originalIndex }))
    .filter(({ brn }) => !typeFilter || brn.type === typeFilter)
    .slice(0, limit ?? Infinity)

  const styleProps: Partial<TextInputProps> = brnStyleProps[sourcePrefix] ?? brnStyleProps.business_reg_numbers

  return (
    <>
      {list.map(({ brn, originalIndex }) => {
        const helperText = brn.type === 'gs_tax_number' ? formatTaxIdentifierHintText(translate, brn.iso2_code) : null
        return (
          <Fragment key={`${brn.code}-${originalIndex}`}>
            <TextInput
              label={`resources.orgs.business_reg_labels.${brn.code || ''}`}
              source={`${sourcePrefix}[${originalIndex}].number`}
              helperText={helperText || undefined}
              skipHelperTextTranslate={true}
              {...(styleProps ?? {})}
            />
            <TextInput
              source={`${sourcePrefix}[${originalIndex}].code`}
              defaultValue={brn.code}
              style={{ display: 'none' }}
            />
            <TextInput
              source={`${sourcePrefix}[${originalIndex}].type`}
              defaultValue={brn.type}
              style={{ display: 'none' }}
            />
          </Fragment>
        )
      })}
    </>
  )
}

const OrgContentEdit = ({ accessRights: { allowView, allowCreate, allowEdit, allowDelete }, ...props }) => {
  const translate = useTranslate()
  const orgCountryIso2 = useSelector(orgSelectors.getOrgIso2)
  const isAdmin = useSelector((state) => Boolean(getRoleFromState(state)?.is_admin))
  const isStaff = useSelector(authSelectors.getIsStaff)
  const isConnectedToBrighte = useSelector(authSelectors.getBrighteConnected)
  const showBrighteId = window.location.href.includes('show_brighte_id=true')
  const uiKey = 'control.company.business_info.fields.'
  const translatedSectors = useMemo(
    () =>
      SERVICE_OFFERING_OPTIONS.map((o) => ({
        ...o,
        name: translate(o.name),
      })),
    [translate]
  )
  return (
    <UiContainer uiKey={uiKey}>
      <ComponentVersionsInherit versions={ComponentVersions_3_0}>
        <Edit {...props}>
          <SimpleForm
            redirect={'/control'}
            validate={(values) => validateOrgContentEdit(translate, values)}
            defaultValue={generateDefaultValueFunc(contentFields)}
            disabled={!allowEdit}
          >
            <div style={{ display: 'flex', width: '100%', flexWrap: 'wrap' }}>
              <TextInput label="resources.orgs.fields.name" source="name" style={inputStyle} />
              <FormDataConsumer>
                {({ formData, ...rest }) => {
                  const record = (rest as { record?: { business_reg_numbers?: BusinessRegNumber[] } }).record
                  const businessRegNumbers = (formData.business_reg_numbers ??
                    record?.business_reg_numbers ??
                    []) as BusinessRegNumber[]
                  return (
                    <BusinessRegistrationNumberInput
                      businessRegNumbers={businessRegNumbers}
                      typeFilter="gs_tax_number"
                      limit={1}
                    />
                  )
                }}
              </FormDataConsumer>
              {orgCountryIso2 === 'GB' && (
                <TextInput
                  label={translate('MCS Accreditation Number')}
                  source="mcs_accreditation_number"
                  style={inputStyle}
                />
              )}
              <TextInput
                label="resources.orgs.fields.sales_phone_number"
                source="sales_phone_number"
                type="tel"
                style={inputStyle}
              />
              <TextInput
                label="resources.orgs.fields.company_email"
                source="company_email"
                type="email"
                style={inputStyle}
              />

              <TextInput label="resources.orgs.fields.company_website" source="company_website" style={inputStyle} />

              <TextInput label="resources.orgs.fields.address" source="address" style={inputStyle} />
              <TextInput label="resources.orgs.fields.locality" source="locality" style={inputStyle} />

              <div style={{ width: 256, marginRight: 40 }}>
                <CustomField
                  component={TextInput}
                  label="resources.orgs.fields.state"
                  name="state"
                  source="state"
                  style={{ ...shortInputStyle, marginRight: 40 }}
                  {...props}
                />
                <CustomField
                  component={TextInput}
                  label="resources.orgs.fields.zip"
                  name="zip"
                  source="zip"
                  style={shortInputStyle}
                  {...props}
                />
              </div>
              <div style={{ width: 256, marginRight: 40 }}>
                <ReferenceInput
                  label="resources.orgs.fields.country"
                  name="country"
                  source="country"
                  reference="countries"
                  optionValueField="url"
                  sort={{ field: 'name', order: 'ASC' }}
                  perPage={1000}
                  allowEmpty={isAdmin}
                  {...props}
                >
                  <AutocompleteInput
                    source="country"
                    optionText="name"
                    optionValue="url"
                    allowDuplicates={true}
                    options={{
                      style: inputStyle,
                      containerStyle: inputStyle,
                      placeholder: 'Enter a country',
                    }}
                  />
                </ReferenceInput>
              </div>
              <UiContainer
                uiKey={uiKey + 'color_highlight'}
                style={{ position: 'relative', ...inputStyle, zIndex: 91 }}
              >
                <CustomField
                  component={isAdmin ? ColorInput : TextInput}
                  label={'resources.orgs.fields.color_highlight'}
                  name="color_highlight"
                  source="color_highlight"
                  {...props}
                />
                <FormDataConsumer>
                  {({ formData }) => <ShowHighLightColor value={formData.color_highlight} />}
                </FormDataConsumer>
              </UiContainer>
              <div style={{ minWidth: 256, marginRight: 40, marginTop: -6 }}>
                <FormControl style={{ width: '100%' }} margin="dense">
                  <CheckboxMultiSelectInput
                    label="Service Offering"
                    id="service-offering"
                    source={'service_offering'}
                    choices={translatedSectors}
                    color={'secondary'}
                    fullWidth
                  />
                </FormControl>
              </div>
              {isStaff && isConnectedToBrighte && showBrighteId && (
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                  <h4>Brighte Vendor Id</h4>
                  <p style={{ marginBottom: '0px' }}>
                    <strong>Warning:</strong> The Brighte vendor id is a key piece of the Brighte intgration. Please do
                    not change this unless you're certain you're changing it to the correct value.
                  </p>
                  <TextInput
                    label="Brighte Vendor Id"
                    source="brighte_vendor_id"
                    type="tel"
                    style={inputStyle}
                    disabled={!isStaff}
                  />
                </div>
              )}
            </div>
            <UiContainer uiKey={uiKey + 'about_content'}>
              <>
                <Typography variant="body1SemiBold">{translate('About Us')}</Typography>

                <FormDataConsumer>
                  {({ formData, ...rest }) =>
                    renderDraftEditor(formData.about_content) && (
                      <FroalaEditorWithAboutContent record={formData} {...rest} />
                    )
                  }
                </FormDataConsumer>

                <FormDataConsumer>
                  {({ formData, ...rest }) =>
                    renderRawHtmlEditor(formData.about_content) && (
                      <TextInput
                        label="resources.orgs.fields.about_content"
                        multiline
                        source="about_content"
                        {...rest}
                      />
                    )
                  }
                </FormDataConsumer>
              </>
            </UiContainer>
            <div style={{ clear: 'both', height: 20 }} />
            <Typography variant="body1SemiBold">{translate('Social Media Links')}</Typography>
            <Typography variant="body2" style={{ marginBottom: 16, color: '#757575' }}>
              {translate('Find us on socials by clicking on the links below')}
            </Typography>
            <div style={{ display: 'flex', width: '100%', flexWrap: 'wrap' }}>
              <TextInput
                label="LinkedIn"
                source="org_configs.business_info.social_media_links.linkedin"
                style={inputStyle}
                placeholder="https://linkedin.com/company/..."
              />
              <TextInput
                label="Facebook"
                source="org_configs.business_info.social_media_links.facebook"
                style={inputStyle}
                placeholder="https://facebook.com/..."
              />
              <TextInput
                label="Twitter / X"
                source="org_configs.business_info.social_media_links.twitter"
                style={inputStyle}
                placeholder="https://twitter.com/..."
              />
              <TextInput
                label="Instagram"
                source="org_configs.business_info.social_media_links.instagram"
                style={inputStyle}
                placeholder="https://instagram.com/..."
              />
              <TextInput
                label="YouTube"
                source="org_configs.business_info.social_media_links.youtube"
                style={inputStyle}
                placeholder="https://youtube.com/..."
              />
              <TextInput
                label="TikTok"
                source="org_configs.business_info.social_media_links.tiktok"
                style={inputStyle}
                placeholder="https://tiktok.com/..."
              />
              <TextInput
                label="Pinterest"
                source="org_configs.business_info.social_media_links.pinterest"
                style={inputStyle}
                placeholder="https://pinterest.com/..."
              />
              <TextInput
                label="Yelp"
                source="org_configs.business_info.social_media_links.yelp"
                style={inputStyle}
                placeholder="https://yelp.com/..."
              />
            </div>
            <div style={{ clear: 'both', height: 20 }} />
            <Typography variant="body1SemiBold">{translate('Logo')}</Typography>
            <ImageFieldClearable
              label="resources.orgs.fields.logo_file_contents"
              source="logo_file_contents"
              maxWidth={200}
            />
            {isAdmin && (
              <ImageInput
                source="logo_file_contents"
                label="resources.orgs.fields.logo_file_contents"
                accept="image/*"
                placeholder={
                  <Typography variant="body2">
                    {translate('Click to upload or drag & drop an image file here')}
                  </Typography>
                }
              >
                <ImageField source="src" title="title" />
              </ImageInput>
            )}
          </SimpleForm>
        </Edit>
      </ComponentVersionsInherit>
    </UiContainer>
  )
}

export default OrgContentEdit
export { BusinessRegistrationNumberInput, type BusinessRegNumber }
